// Canonical design tokens — the single source of truth for color/spacing/typography.
// src/styles.js and src/constants/colors.js derive their (legacy-shaped) public
// exports from these primitives; consumers should keep importing from those two
// files, not from here, until an opportunistic per-file migration happens.

// Tailwind 500/700-shade hexes for every colour NAME used across COLOURS,
// params.js, and the per-device *Color fields.
export const palette = {
  slate: { 500: '#64748b', 700: '#334155' },
  gray: { 500: '#6b7280', 700: '#374151' },
  zinc: { 500: '#71717a', 700: '#3f3f46' },
  neutral: { 500: '#737373', 700: '#404040' },
  stone: { 500: '#78716c', 700: '#44403c' },
  red: { 500: '#ef4444', 700: '#b91c1c' },
  orange: { 500: '#f97316', 700: '#c2410c' },
  amber: { 500: '#f59e0b', 700: '#b45309' },
  yellow: { 500: '#eab308', 700: '#a16207' },
  lime: { 500: '#84cc16', 700: '#4d7c0f' },
  green: { 500: '#22c55e', 700: '#15803d' },
  emerald: { 500: '#10b981', 700: '#047857' },
  teal: { 500: '#14b8a6', 700: '#0f766e' },
  cyan: { 500: '#06b6d4', 700: '#0e7490' },
  sky: { 500: '#0ea5e9', 700: '#0369a1' },
  blue: { 500: '#3b82f6', 700: '#1d4ed8' },
  indigo: { 500: '#6366f1', 700: '#4338ca' },
  violet: { 500: '#8b5cf6', 700: '#6d28d9' },
  purple: { 500: '#a855f7', 700: '#7e22ce' },
  fuchsia: { 500: '#d946ef', 700: '#a21caf' },
  pink: { 500: '#ec4899', 700: '#be185d' },
  rose: { 500: '#f43f5e', 700: '#be123c' },
};

// Semantic colour roles. `hex` is the literal value carried today by
// COLOURS.<role>Hex / coloursHex.<role> — it is independently defined and does
// not always equal palette[name][500] (e.g. "warning"'s name is "yellow" but
// its hex is amber's); preserved as-is rather than reconciled.
export const semantic = /** @type {const} */ ({
  good: { name: 'green', hex: '#66BB6A' },
  error: { name: 'red', hex: '#ef4444' },
  warning: { name: 'yellow', hex: '#f59e0b' },
  cardDecoration: { name: 'slate', hex: '#737373' },
  title: { name: 'stone', hex: '#78716c' },
  default: { name: 'sky', hex: '#0ea5e9' },
  primary: { hex: '#3498FF' },
  border: { hex: '#d1d5db' },
});

export const status = {
  connected: '#009933',
  connecting: '#cccc00',
  disconnected: '#cc3300',
  waiting: '#ff9966',
};

export const surface = {
  // CSS keyword, distinct from the "#ffffff" hex literal below — kept separate
  // so each preserves its own exact original string value.
  cardBg: 'white',
  white: '#ffffff',
  subtle: '#f1f5f9',
};

export const space = {
  radius: { sm: '5px', card: '6px', group: '7px' },
  pad: { tight: '0.5em', card: '1em', grid: '1.1em', heading: '10px', button: '6px' },
  gap: { sm: 8, md: 12 },
};

export const type = {
  size: { sm: 12 },
  weight: { medium: 500 },
};
